/**
 * Curated PostHog Event Schema — Alchema Analytics Taxonomy
 *
 * BUDGET: ~20 events/day/user (scaling tier)
 * RULE: Only track events defined here. No ad-hoc capture() calls.
 * Client-side: use trackEvent() from ~/lib/posthog-client
 * Server-side: use captureServerEvent() from ~/lib/posthog-server
 */

// ---------------------------------------------------------------------------
// 1. Curated events (scaling taxonomy — 26 events)
// ---------------------------------------------------------------------------

export const ANALYTICS_EVENTS = {
  // Auth & Onboarding (2 events)
  USER_SIGNED_UP: "user_signed_up",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // Resume & Job (3 events)
  RESUME_UPLOADED: "resume_uploaded",
  JOB_SEARCHED: "job_searched",
  JOB_VIEWED: "job_viewed",

  // AI Actions (2 events)
  AI_TAILOR_STARTED: "ai_tailor_started",
  AI_TAILOR_COMPLETED: "ai_tailor_completed",

  // Application (1 event)
  APPLICATION_SUBMITTED: "application_submitted",

  // Credits (2 events)
  CREDIT_USED: "credit_used",
  CREDIT_EXHAUSTED: "credit_exhausted",

  // Revenue (3 events)
  UPGRADE_CLICKED: "upgrade_clicked",
  SUBSCRIPTION_STARTED: "subscription_started",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",

  // Engagement (2 events)
  FEATURE_USED: "feature_used",
  SESSION_STARTED: "session_started",

  // Navigation (1 event) — feeds funnel analysis
  PAGE_VIEWED: "page_viewed",

  // Errors & Satisfaction (3 events)
  AI_ACTION_FAILED: "ai_action_failed",
  PAGE_ERROR_SHOWN: "page_error_shown",
  FEEDBACK_SUBMITTED: "feedback_submitted",

  // Campaign Attribution (2 events)
  CAMPAIGN_LANDED: "campaign_landed",
  CTA_CLICKED: "cta_clicked",

  // Credit Packs (1 event)
  CREDIT_PACK_PURCHASED: "credit_pack_purchased",

  // Marketing & Growth (3 events)
  REFERRAL_SHARED: "referral_shared",
  REFERRAL_REDEEMED: "referral_redeemed",
  JOB_ALERT_CREATED: "job_alert_created",

  // Blog Content Analytics (5 events — per D-12 Phase 64)
  BLOG_PAGE_VIEW: "blog_page_view",
  BLOG_CTA_CLICK: "blog_cta_click",
  SIGNUP_FROM_BLOG: "signup_from_blog",
  BLOG_SCROLL_DEPTH: "blog_scroll_depth",
  BLOG_TIME_ON_PAGE: "blog_time_on_page",

  // Account Lifecycle (1 event)
  ACCOUNT_DELETED: "account_deleted",
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// ---------------------------------------------------------------------------
// 2. Typed property interfaces per event
// ---------------------------------------------------------------------------

export type AnalyticsLocale = "en" | "de" | "fr" | "es" | "it" | "nl" | "pl" | "fi" | "da" | "sv";
export type AnalyticsTier = "free" | "student" | "pro" | "premium" | "unknown";
export type AnalyticsRoute = "home" | "pricing" | "jobs" | "job_detail" | "resumes" | "resume_detail" | "applications" | "blog" | "auth" | "settings" | "other";
export type BlogPageType = "country" | "city" | "role" | "visa" | "university" | "editorial";

export function canonicalAnalyticsRoute(pathname: string): AnalyticsRoute {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (/^\/jobs\/[^/]+/.test(pathname)) return "job_detail";
  if (pathname.startsWith("/jobs")) return "jobs";
  if (/^\/resumes\/[^/]+/.test(pathname)) return "resume_detail";
  if (pathname.startsWith("/resumes")) return "resumes";
  if (pathname.startsWith("/applications")) return "applications";
  if (pathname.startsWith("/blog")) return "blog";
  if (/^\/(auth|login|register|onboarding)/.test(pathname)) return "auth";
  if (pathname.startsWith("/settings")) return "settings";
  return "other";
}

export function asBlogPageType(value: unknown): BlogPageType | undefined {
  return typeof value === "string" && ["country", "city", "role", "visa", "university", "editorial"].includes(value)
    ? value as BlogPageType
    : undefined;
}

export function asAnalyticsLocale(value: unknown): AnalyticsLocale | undefined {
  return typeof value === "string" && ["en", "de", "fr", "es", "it", "nl", "pl", "fi", "da", "sv"].includes(value)
    ? value as AnalyticsLocale
    : undefined;
}

export function asAnalyticsTier(value: unknown): AnalyticsTier {
  return typeof value === "string" && ["free", "student", "pro", "premium"].includes(value)
    ? value as AnalyticsTier
    : "unknown";
}

export function classifyCampaignSource(value: unknown): "organic" | "paid" | "email" | "social" | "partner" | "other" {
  if (typeof value !== "string") return "other";
  const normalized = value.toLowerCase();
  if (["google", "bing", "organic"].includes(normalized)) return "organic";
  if (["cpc", "ppc", "paid"].includes(normalized)) return "paid";
  if (["email", "newsletter"].includes(normalized)) return "email";
  if (["linkedin", "instagram", "facebook", "social"].includes(normalized)) return "social";
  if (["partner", "affiliate"].includes(normalized)) return "partner";
  return "other";
}
export type AnalyticsAction =
  | "resume-parse" | "resume-tailor" | "cover-letter" | "salary-analysis" | "interview-prep" | "job-search"
  | "bullet-rewrite" | "summary-generation" | "skill-suggestion" | "salary-benchmark" | "salary-compare"
  | "negotiation-script" | "qa-answer" | "story-generation" | "deep-match" | "pdf_export" | "pdf_export_fallback"
  | "markdown_export" | "candidate_ai_notice" | "student_verification" | "low_credit_banner" | "job_tracking_limit"
  | "human_review_preference" | "ai-tailor" | "cover-letter-generated" | "ai_action" | "credit_exhaustion_wait"
  | "job-extraction" | "email-classify" | "company-brief" | "market-intel";

export interface EventProperties {
  user_signed_up: { method: "email" | "google"; locale?: AnalyticsLocale };
  onboarding_completed: { has_resume: boolean; steps_completed: number };
  resume_uploaded: { file_type: "pdf" | "docx" | "txt"; file_size_kb: number; status?: "ok" | "failed" };
  job_searched: { result_count?: number; has_query: boolean; has_filters: boolean };
  job_viewed: { source?: "linkedin" | "indeed" | "stepstone" | "xing" | "adzuna" | "jooble" | "remoteok" | "arbeitnow" | "other" };
  ai_tailor_started: { action?: AnalyticsAction; has_job_title?: boolean; has_company?: boolean };
  ai_tailor_completed: { action?: AnalyticsAction; has_job_title?: boolean; has_company?: boolean; match_score?: number; duration_ms?: number; stories_count?: number };
  application_submitted: { method?: "manual" | "auto" | "email" | "portal" | "other" };
  credit_used: { action: AnalyticsAction; cost: number; balance_after: number; source?: "pack" | "subscription" };
  credit_exhausted: { action_attempted: AnalyticsAction; tier: AnalyticsTier };
  upgrade_clicked: { source: "pricing_page" | "low_credit_banner" | "job_tracking_limit" | "credit_exhaustion_modal"; current_tier?: AnalyticsTier; target_tier?: AnalyticsTier };
  subscription_started: { plan: AnalyticsTier; price_eur?: number; interval?: "monthly" | "annual" };
  subscription_cancelled: { plan: AnalyticsTier };
  feature_used: { feature: AnalyticsAction; state?: "shown" | "enabled" | "disabled" | "completed" | "learn_more" };
  session_started: { has_referrer: boolean; locale?: AnalyticsLocale };
  page_viewed: { route: AnalyticsRoute };
  ai_action_failed: { action: AnalyticsAction; error_type: "runtime" | "network" | "provider" | "validation" | "unknown"; error_code: "unknown" | "timeout" | "network" | "rate_limited" | "upstream_rejected" | "validation" | "parse_failed" | "provider_failed" | "gateway_failed" | "circuit_open" | "retry_exhausted" | "configuration_missing"; duration_ms?: number; retries_exhausted?: boolean };
  page_error_shown: { error_type: "runtime" | "network" | "not_found" | "server"; route: AnalyticsRoute };
  feedback_submitted: { type: "bug" | "feature" | "general" | "rating"; rating?: number; has_context?: boolean };
  credit_pack_purchased: { pack_type: "starter" | "boost" | "power"; credits: number };
  campaign_landed: { source: "organic" | "paid" | "email" | "social" | "partner" | "other"; landing_route: "home" | "pricing" | "blog" | "other" };
  cta_clicked: { cta: "signup" | "pricing" | "feature" | "buy_pack"; location: "pricing_page" | "low_credit_banner" | "credit_exhaustion_modal" | "blog" | "other" };
  referral_shared: { method: "copy" | "whatsapp" | "email" };
  referral_redeemed: { referee_tier?: AnalyticsTier };
  job_alert_created: { has_title_query: boolean; has_location: boolean; frequency: "daily" | "weekly" };
  blog_page_view: { pageType: BlogPageType; locale: AnalyticsLocale };
  blog_cta_click: { cta_type: "signup" | "pricing" | "feature"; pageType: BlogPageType };
  signup_from_blog: { source_pageType: BlogPageType };
  blog_scroll_depth: { threshold: 25 | 50 | 75 | 100; pageType: BlogPageType };
  blog_time_on_page: { threshold: 30 | 60 | 120; pageType: BlogPageType };
  account_deleted: Record<string, never>;
}

// ---------------------------------------------------------------------------
// 3. ORPC procedure → event mapping (used by server.ts middleware)
// ---------------------------------------------------------------------------

/**
 * Maps ORPC procedure paths to curated events.
 * Only procedures with a direct curated-event mapping are included.
 */
export const PROCEDURE_EVENT_MAP: Record<string, AnalyticsEvent> = {
  "resume.parseUpload": ANALYTICS_EVENTS.RESUME_UPLOADED,
};

/**
 * Set of ORPC procedure paths that consume AI credits.
 * Used by the server-side capture wrapper to attach AI properties (duration_ms).
 */
export const AI_PROCEDURE_NAMES = new Set<string>([
  "resume.generateBullets",
  "resume.generateSummary",
  "resume.generateSkillDescription",
  "resume.parseUpload",
  "job.analyze",
  "job.tailor",
  "coverLetter.generate",
  "coverLetter.regenerate",
  "salary.benchmark",
  "salary.compare",
  "salary.negotiate",
  "interview.generateForJob",
  "interview.submitAnswer",
  "interview.getCompanyBrief",
  "interview.questionBank",
]);

/**
 * Successful ORPC procedures whose entire response is synthetic/AI-assisted.
 *
 * This is deliberately separate from AI_PROCEDURE_NAMES: that set retains its
 * credit/rate-limit/analytics meaning. Mixed-source procedures such as
 * answerQuestion mark only their AI value at the producer.
 */
export const AI_OUTPUT_PROCEDURE_NAMES = new Set<string>([
  ...AI_PROCEDURE_NAMES,
  "resume.editWithAi",
  "resume.translate",
  "deepMatch.analyze",
]);
